import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import type { WizardConfig } from '../types/wizard'

export default function SuccessPage() {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')
  const [config, setConfig] = useState<WizardConfig | null>(null)
  const [embedCode, setEmbedCode] = useState('')
  const [copied, setCopied] = useState(false)
  const [deploymentProgress, setDeploymentProgress] = useState(0)
  const [deploymentComplete, setDeploymentComplete] = useState(false)

  useEffect(() => {
    // Load configuration from session storage
    const savedConfig = sessionStorage.getItem('wizardConfig')
    if (savedConfig) {
      const parsedConfig = JSON.parse(savedConfig)
      setConfig(parsedConfig)

      // Generate embed code
      // @ts-ignore - Vite env is available at runtime
      const widgetUrl = import.meta.env?.VITE_WIDGET_URL || 'https://nice-coast-035cec01e.3.azurestaticapps.net/widget.js'
      const code = `<!-- HellaBots Chat Widget -->
<script src="${widgetUrl}"></script>
<script>
  HellaBots.init({
    clientId: "${parsedConfig.clientId}",
    primaryColor: "${parsedConfig.primaryColor}"
  });
</script>`
      setEmbedCode(code)
    }

    // Simulate deployment progress
    const interval = setInterval(() => {
      setDeploymentProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval)
          setDeploymentComplete(true)
          return 100
        }
        return prev + 2
      })
    }, 300) // Complete in ~15 seconds

    return () => clearInterval(interval)
  }, [])

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <section className="section bg-white flex-1">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Payment Successful!
              </h1>

              <p className="text-xl text-gray-700 mb-2">
                Welcome to HellaBots, {config?.agentName || 'there'}!
              </p>
              <p className="text-gray-600">
                Order ID: {sessionId?.substring(0, 20)}...
              </p>
            </div>

            {/* Deployment Progress */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-gray-900">
                  {deploymentComplete ? 'Your Chatbot is Ready!' : 'Deploying Your Chatbot...'}
                </h2>
                <span className="text-sm font-medium text-gray-600">{deploymentProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${deploymentProgress}%` }}
                />
              </div>
              {deploymentComplete && (
                <p className="text-sm text-green-600 mt-2 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Deployment complete!
                </p>
              )}
            </div>

            {/* Embed Code */}
            {config && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Your Embed Code
                </h2>
                <p className="text-gray-600 mb-4">
                  Copy this code and paste it before the closing <code className="bg-gray-200 px-1 rounded">&lt;/body&gt;</code> tag on your website:
                </p>

                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                    <code>{embedCode}</code>
                  </pre>
                  <button
                    onClick={copyToClipboard}
                    className="absolute top-2 right-2 bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-sm flex items-center gap-2"
                  >
                    {copied ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                        Copy Code
                      </>
                    )}
                  </button>
                </div>

                <div className="mt-4 text-sm text-gray-600">
                  <p className="font-medium text-gray-900 mb-2">Installation Tips:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Add this code to every page where you want the chatbot to appear</li>
                    <li>For WordPress: Use a plugin like "Insert Headers and Footers"</li>
                    <li>For Wix/Squarespace: Add to the site-wide footer in settings</li>
                    <li>Test the chatbot in incognito mode to see it as a visitor</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Configuration Summary */}
            {config && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Your Configuration
                </h2>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-gray-500">Client ID</p>
                    <p className="text-gray-900 font-mono">{config.clientId}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Calendly URL</p>
                    <p className="text-gray-900 truncate">{config.calendlyUrl}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Cities</p>
                    <p className="text-gray-900">{config.cities.join(', ')}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Price Ranges</p>
                    <p className="text-gray-900">{config.priceRanges.join(', ')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Next Steps */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What's Next?</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Add the Embed Code</h3>
                    <p className="text-gray-600">Copy the code above and add it to your website. The chatbot will appear immediately.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Check Your Email</h3>
                    <p className="text-gray-600">We've sent setup instructions and dashboard login details to {config?.email}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Monitor Your Leads</h3>
                    <p className="text-gray-600">Access your analytics dashboard to see conversations and HOT leads in real-time.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Manage Subscription</h3>
                    <p className="text-gray-600">
                      Use the{' '}
                      <a href="#" className="text-primary-600 hover:text-primary-700 underline">
                        Stripe Customer Portal
                      </a>{' '}
                      to update billing, download invoices, or cancel anytime.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Support */}
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                Need help? Email us at{' '}
                <a href="mailto:support@hellabots.com" className="text-primary-600 hover:text-primary-700 underline">
                  support@hellabots.com
                </a>
              </p>
              <Link to="/" className="btn-secondary inline-block">
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
